import { MAP_INK, MAP_SELECTED } from "../../components/GeoMap/mapPalette";
import { useMemo } from "react";
import { Box, Paper } from "@mui/material";

import {
  ColorRamp,
  GeoMapCanvas,
  GeoMapPoint,
  SizeRamp,
} from "../../components/GeoMap";
import { getDefaultOptions } from "../../services/Category";
import { Candidate, ContractedProperty } from "./types";

const MAP_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © Geoapify';

const TILE_URL = `${window.origin}/api/geocoding/tiles/{z}/{x}/{y}/?style=klokantech-basic`;

// ⚠️ Module-level so its identity is stable across renders. A new function identity
// tears down and recreates the map — the same note `MapTab.tsx` carries.
const transformTileRequest = (url: string) => {
  if (!url.startsWith(window.origin)) return { url };
  return {
    url,
    headers: getDefaultOptions().headers as Record<string, string>,
  };
};

/**
 * `value` sentinel for the client's own property.
 *
 * ⚠️ `GeoMapPoint` has no field distinguishing one kind of pin from another, and
 * extending the shared component for a single consumer is not worth it — the page
 * already refuses to *add* the client's own id, so what is left is purely visual.
 * A value outside the 0-5 rating range puts it at the far end of the ramp, which is
 * what makes it read as a different pin.
 */
export const CLIENT_PROPERTY_VALUE = -1;

/**
 * Interest rating drives the colour, 0-5. The client's property sits at -1, below
 * every candidate, so it takes the first stop and nothing else can.
 *
 * ⚠️ There was a fourth band here for competitors we could not price, and it is gone
 * on purpose: selecting one now provisions its Booking connector, so every hotel is
 * priceable and a "cannot price" colour would encode a state that no longer exists.
 */
/**
 * Radius by interest rating, so the rating is encoded twice.
 *
 * The subject sits at -1 and unrated competitors at 1, both of which clamp to the
 * first stop — the ramp starts at 1 because nothing derives 0 in practice.
 */
const SIZE_RAMP: SizeRamp = {
  stops: [
    [1, 6],
    [3, 8.5],
    [5, 12],
  ],
};

/** Zoom at which competitor names appear, and how many at once. */
const LABEL_FROM_ZOOM = 15;
const MAX_LABELS = 14;

/**
 * Metres of separation given to competitors that share a coordinate.
 *
 * ⚠️ This is a reachability fix, not cosmetics. Measured on one Paris property's
 * 40 km radius: 119 of 2 197 competitors sit on *byte-identical* coordinates in 54
 * groups, six deep at worst, and 212 fall within an 11 m circle. Multi-unit
 * apartment listings are the cause. Clustering hides it until `clusterMaxZoom`,
 * after which the pins overlap exactly and only the topmost can ever be hovered or
 * clicked — and supercluster cannot separate identical points at any zoom, so
 * zooming is not a way out either.
 *
 * 12 m is under the accuracy anyone should read into a geocoded hotel and is enough
 * to separate 8 px pins at street zoom.
 */
const COLOCATED_SPREAD_M = 12;
const METRES_PER_DEGREE = 111_320;

/**
 * Fan competitors that share a coordinate onto a small circle around it.
 *
 * ⚠️ Deterministic — the offset comes from the index within the group, not from
 * anything random — so a pin does not move between renders, which would break
 * hovering and make the map feel unstable.
 */
const spreadColocated = (points: GeoMapPoint[]): GeoMapPoint[] => {
  const groups = new Map<string, GeoMapPoint[]>();
  points.forEach((point) => {
    const key = `${point.latitude.toFixed(5)},${point.longitude.toFixed(5)}`;
    const group = groups.get(key);
    if (group) group.push(point);
    else groups.set(key, [point]);
  });

  const spread: GeoMapPoint[] = [];
  groups.forEach((group) => {
    if (group.length === 1) {
      spread.push(...group);
      return;
    }
    const radius = COLOCATED_SPREAD_M / METRES_PER_DEGREE;
    group.forEach((point, index) => {
      const angle = (2 * Math.PI * index) / group.length;
      spread.push({
        ...point,
        latitude: point.latitude + radius * Math.sin(angle),
        longitude:
          point.longitude +
          (radius * Math.cos(angle)) /
            Math.max(Math.cos((point.latitude * Math.PI) / 180), 0.01),
      });
    });
  });
  return spread;
};

/**
 * Interest rating 1→5 as an **ordinal ramp**: one hue, light to dark.
 *
 * ⚠️ Rating 5 is the darkest step of the same hue rather than an accent colour, and
 * that is deliberate. A hue break at the top would stop the scale reading as an
 * order, and the colours it would want — amber, orange — are the reserved status
 * palette, which is where "already tracked" lives. Prominence for the top band comes
 * from luminance *and* from `SIZE_RAMP`, so the rating is encoded twice.
 *
 * ⚠️ Steps validated, not chosen by eye: monotone lightness, all adjacent gaps
 * ≥ 0.06 L, single hue (4° spread), and the lightest step clears 2:1 against the
 * white casing every pin sits on — which is the surface that matters here, since the
 * basemap tiles are not ours to predict.
 *
 * The subject keeps its stop for the cluster average's sake, but is drawn as a ring
 * by `subjectPointId`, so its colour is form-independent.
 */
export const RAMP: ColorRamp = {
  stops: [
    [CLIENT_PROPERTY_VALUE, MAP_INK],
    // ⚠️ `InterestRating.NONE` is 0 — "0 - irrelevant" — and without a stop of its own
    // it interpolated halfway between the client property's near-black and the
    // lightest blue, which reads as a subject pin rather than as the bottom of the
    // scale. Deliberately the *same* colour as 1 rather than a new lighter band: the
    // lightest step is already what binds the 2:1 contrast against the white casing,
    // so a lighter one would break the validated ramp to distinguish a value that
    // `derive_interest_rating` cannot produce (its `by_stars` floor is 1) and that no
    // competitor currently has. Same-colour keeps 0 at the correct end and keeps
    // cluster averages between 0 and 1 flat instead of muddy.
    //
    // ⚠️ `SIZE_RAMP` needs no equivalent, and the asymmetry is the point: its lowest
    // stop is 1, so 0 *clamps* to the smallest radius. This ramp has a stop below 0,
    // which is the only reason 0 interpolated here instead of clamping.
    [0, "#86b6ef"],
    [1, "#86b6ef"],
    [2, "#5598e7"],
    [3, "#2a78d6"],
    [4, "#1c5cab"],
    [5, "#0d366b"],
  ],
};

/**
 * Already tracked — a state, so it comes from the status palette rather than the
 * ramp, and never carries the meaning alone.
 *
 * ⚠️ Measured against the ramp: ΔE 29–39 in normal vision and 25–37 under deutan
 * and protan, but only **6.2 under tritan** against the mid blue, because
 * tritanopia is exactly blue-green confusion. That is inside the band the method
 * allows only with a secondary encoding, which is why the canvas also gives these
 * pins a heavier ring and why the legend exists.
 */
export const TRACKED_COLOR = MAP_SELECTED;

type Props = {
  property: ContractedProperty;
  candidates: Candidate[];
  candidatesSettled: boolean;
  /** Sweep competitor ids already in this property's compset. */
  selectedIds: readonly number[];
  highlightedId: number | null;
  onPointClick: (id: number) => void;
  onPointHover: (id: number | null) => void;
};

const CompetitorMap = ({
  property,
  candidates,
  candidatesSettled,
  selectedIds,
  highlightedId,
  onPointClick,
  onPointHover,
}: Props) => {
  const points = useMemo<GeoMapPoint[]>(() => {
    const competitors: GeoMapPoint[] = candidates
      .filter(
        (candidate) =>
          candidate.latitude !== null && candidate.longitude !== null
      )
      .map((candidate) => ({
        id: candidate.id,
        label: candidate.name,
        latitude: candidate.latitude as number,
        longitude: candidate.longitude as number,
        value: candidate.interestRating,
      }));

    // ⚠️ Competitors only. The client's own property keeps its true coordinate: it
    // is what the viewport is fitted to and the one pin whose position is a fact
    // rather than a rendering convenience.
    const result = spreadColocated(competitors);

    if (property.latitude !== null && property.longitude !== null) {
      result.push({
        // ⚠️ Negative so it can never collide with a `competitor.id`, which is
        // what `onPointClick` reports and what the page adds by.
        id: -property.propertyId,
        label: property.propertyName,
        latitude: property.latitude,
        longitude: property.longitude,
        value: CLIENT_PROPERTY_VALUE,
      });
    }
    return result;
  }, [candidates, property]);

  return (
    <Paper sx={{ overflow: "hidden" }}>
      <Box sx={{ height: { xs: 360, md: 560 } }}>
        <GeoMapCanvas
          points={points}
          colorRamp={RAMP}
          sizeRamp={SIZE_RAMP}
          subjectPointId={-property.propertyId}
          selectedPointIds={selectedIds}
          selectedColor={TRACKED_COLOR}
          labelFromZoom={LABEL_FROM_ZOOM}
          maxLabels={MAX_LABELS}
          tileUrlTemplate={TILE_URL}
          attribution={MAP_ATTRIBUTION}
          transformRequest={transformTileRequest}
          onPointClick={(id) => onPointClick(id)}
          onPointHover={(id) => onPointHover(id)}
          highlightedPointId={highlightedId}
          // Switching property switches what the map is *about*, so the viewport has
          // to follow — the auto-fit is one-shot by default so that panning is not
          // undone every time the candidate list refreshes.
          //
          // ⚠️ Withheld until the candidates have settled. `points` is rebuilt the
          // moment `property` changes, but react-query has already dropped the old
          // page, so at that instant the only point is the property pin — and a fit
          // on one point is a zoom-12 close-up with the compset outside the
          // viewport. Passing `undefined` skips the latch reset, so the fit happens
          // once, on the complete set.
          autoFitKey={candidatesSettled ? property.propertyId : undefined}
          // ⚠️ `fitMinZoom` is what fixes "too wide for a metropole"; the other two
          // barely move it. The fit follows its *bounds*, and over a 40 km search
          // radius the outermost candidate frames the whole city — padding trims a
          // hair off that and `fitMaxZoom` never binds at all, because a spread that
          // wide is nowhere near the ceiling.
          //
          // 12 opens on roughly 10 km around the property, which is the neighbourhood
          // a compset is actually chosen from. The trade is real and worth naming:
          // candidates further out start off screen, and someone comparing against a
          // hotel across the city has to zoom out to see it.
          //
          // ⚠️ All three stay below `LABEL_FROM_ZOOM`. At 15 the names appear, and a
          // first paint of `MAX_LABELS` overlapping labels reads as clutter rather
          // than detail — labels are meant to arrive when someone zooms in on purpose.
          fitPadding={24}
          fitMaxZoom={14}
          fitMinZoom={12}
        />
      </Box>
    </Paper>
  );
};

export default CompetitorMap;
